// Seeding item calculations used by SeedingDetailPage, kept pure so they can be unit-tested

// Return on investment, in percent, of hauling an item from the source market and selling it here.
// fees: { collateralPercentage, iskPerM3, brokerFeePercentage, salesTaxPercentage, sccPercentage }
export function calculateROI(item, { collateralPercentage, iskPerM3, brokerFeePercentage, salesTaxPercentage, sccPercentage = 0 }) {
  if (!item.source_price || !item.current_price) return 0;

  const sourcePrice = parseFloat(item.source_price);
  const currentPrice = parseFloat(item.current_price);
  const volume = parseFloat(item.volume) || 0;

  const collateralCost = sourcePrice * (collateralPercentage / 100);
  const volumeShippingCost = volume * iskPerM3;
  const investment = sourcePrice + collateralCost + volumeShippingCost;

  if (investment === 0) return 0;

  const totalFeePercentage = brokerFeePercentage + salesTaxPercentage + sccPercentage;
  const netRevenue = currentPrice * (1 - (totalFeePercentage / 100));

  return ((netRevenue - investment) / investment) * 100;
}

// Items matching the stocking table's filters; roiOf(item) gives an item's ROI percentage
export function filterItems(items, filters, roiOf) {
  return items.filter(item => {
    if (filters.purchasedPrice === 'with' && !item.purchased_price) return false;
    if (filters.purchasedPrice === 'without' && item.purchased_price) return false;

    // Items without a source or destination price have no ROI, so the ROI range never hides them
    if (item.source_price && item.current_price) {
      const roi = roiOf(item);
      if (filters.roiMin !== '' && roi < parseFloat(filters.roiMin)) return false;
      if (filters.roiMax !== '' && roi > parseFloat(filters.roiMax)) return false;
    }

    const inStockPct = item.target_stock > 0 ? (item.current_stock / item.target_stock) * 100 : 0;
    if (filters.inStockMin !== '' && inStockPct < parseFloat(filters.inStockMin)) return false;
    if (filters.inStockMax !== '' && inStockPct > parseFloat(filters.inStockMax)) return false;

    return true;
  });
}

// Sort state after clicking a column header: a new column sorts ascending, then descending, then unsorted
export function nextSortConfig(sortConfig, key) {
  if (sortConfig.key !== key) return { key, direction: 'asc' };
  if (sortConfig.direction === 'asc') return { key, direction: 'desc' };
  if (sortConfig.direction === 'desc') return { key: null, direction: 'asc' };
  return sortConfig;
}

// Items in the stocking table's sort order; roiOf(item) gives an item's ROI percentage
export function sortItems(items, sortConfig, roiOf) {
  if (!sortConfig.key) return items;

  return [...items].sort((a, b) => {
    let aValue, bValue;

    switch (sortConfig.key) {
      case 'item_name':
        aValue = a.item_name.toLowerCase();
        bValue = b.item_name.toLowerCase();
        break;
      case 'to_buy':
        aValue = Math.max(0, a.target_stock - a.current_stock);
        bValue = Math.max(0, b.target_stock - b.current_stock);
        break;
      case 'current_stock':
        aValue = a.current_stock;
        bValue = b.current_stock;
        break;
      case 'avg_consumption':
        aValue = parseFloat(a.avg_consumption) || 0;
        bValue = parseFloat(b.avg_consumption) || 0;
        break;
      case 'target_stock':
        aValue = a.target_stock;
        bValue = b.target_stock;
        break;
      case 'stock_percentage':
        aValue = a.target_stock > 0 ? (a.current_stock / a.target_stock) * 100 : 0;
        bValue = b.target_stock > 0 ? (b.current_stock / b.target_stock) * 100 : 0;
        break;
      case 'last_restock_days_ago':
      case 'restock_interval_days':
      case 'days_of_cover':
        // Never restocked / no sales sort as the longest duration
        aValue = a[sortConfig.key] ?? Infinity;
        bValue = b[sortConfig.key] ?? Infinity;
        break;
      case 'source_price':
        aValue = parseFloat(a.source_price) || 0;
        bValue = parseFloat(b.source_price) || 0;
        break;
      case 'current_price':
        aValue = parseFloat(a.current_price) || 0;
        bValue = parseFloat(b.current_price) || 0;
        break;
      case 'profit_percentage':
        aValue = roiOf(a);
        bValue = roiOf(b);
        break;
      case 'purchased_price':
        aValue = parseFloat(a.purchased_price) || 0;
        bValue = parseFloat(b.purchased_price) || 0;
        break;
      case 'list_price':
        aValue = parseFloat(a.list_price) || 0;
        bValue = parseFloat(b.list_price) || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
}
